import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  Button,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  Divider,
  Tooltip,
  Stack,
  ButtonGroup,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import BrowserUpdatedIcon from "@mui/icons-material/BrowserUpdated";
import InputOutlinedIcon from "@mui/icons-material/InputOutlined";
import { useEffect, useState } from "react";
import { useCodelist } from "../../hooks/useCodelist";
import { AlertDialog } from "../common/AlertDailog";
import { useEntity } from "./MetadataPage/useEntity";
import { useSnackbar } from "../../hooks/useSnackbar";
import { useTransformation } from "../../hooks/useTransformation";
import { useAppDispatch } from "../../store";
import { showLoader, hideLoader } from "../../store/slices/loaderSlice";
import Loader from "../common/Loader/Loader";

const TransformationPage: React.FunctionComponent<any> = () => {
  const [fields, setFields] = useState([]);
  const [selectedFieldIndex, setSelectedFieldIndex] = useState<any>(null);
  const [entity, setEntity] = useState("");
  const [entityList, setEntityList] = useState([]);
  const [codelist, setCodelist] = useState([]);
  const [entityData, setEntityData] = useState<any>({});
  const [entityDetails, setEntityDetails] = useState<any>({});
  const [openCodelistDialog, setOpenCodelistDialog] = useState(false);
  const [codelistValues, setCodelistValues] = useState<any>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [openChangeAlert, setOpenChangeAlert] = useState(false);
  const [nextEntity, setNextEntity] = useState("");
  const [isUpdate, setIsUpdate] = useState(false);
  const { getAllCodelists, allCodelistData, getCodelistValues } = useCodelist();
  const { showSuccess, handleError } = useSnackbar();
  const {
    refetchGetAllEntities,
    getAllEntitiesData,
    getSelectedEntityDetails,
    selectEntityData,
  } = useEntity();
  const dispatch = useAppDispatch();
  const {
    createTransformation,
    createTransformationData,
    createTransformationIsLoading,
    createTransformationIsError,
    createTransformationError,
    createTransformationIsSuccess,
    updateTransformation,
    updateTransformationData,
    updateTransformationIsLoading,
    updateTransformationIsError,
    updateTransformationError,
    updateTransformationIsSuccess,
    getTransformation,
  } = useTransformation();

  useEffect(() => {
    refetchGetAllEntities();
    getAllCodelists({ is_audit: false });
  }, []);

  useEffect(() => {
    if (getAllEntitiesData && getAllEntitiesData.length > 0) {
      setEntityList(getAllEntitiesData);
    }
  }, [getAllEntitiesData]);

  useEffect(() => {
    if (allCodelistData && allCodelistData?.count > 0) {
      const data = allCodelistData?.result?.map((ele: any) => {
        return {
          ...ele,
          id: ele.code_list_code,
        };
      });
      setCodelist(data);
    } else if (allCodelistData?.count == 0) {
      setCodelist([]);
    }
  }, [allCodelistData]);

  useEffect(() => {
    if (fields.length > 0) {
      if (entity) {
        const initializeFields = (entity: string) => {
          const initialFieldValues: any = {};
          fields?.forEach((field: any, index: number) => {
            initialFieldValues[index] = {
              ...field,
              metadata: {
                ...field.metadata,
              },
            };
          });
          setEntityData({ [entity]: initialFieldValues });
        };
        entity?.length > 0 && initializeFields(entity);
      }
    }
  }, [fields]);

  useEffect(() => {
    if (selectEntityData) {
      setEntityDetails(selectEntityData);
      setFields(
        selectEntityData?.fields.map((item: any) => {
          return {
            ...item,
          };
        })
      );
    }
  }, [selectEntityData]);

  useEffect(() => {
    if (createTransformationIsError || updateTransformationIsError) {
      const error: any = createTransformationError ?? updateTransformationError;
      handleError(error);
    }
  }, [createTransformationIsError, updateTransformationIsError]);

  useEffect(() => {
    if (createTransformationIsSuccess || updateTransformationIsSuccess) {
      const successMsg = createTransformationData ?? updateTransformationData;
      showSuccess(successMsg?.details, "create-entity-success");
      setIsDirty(false);
      setIsUpdate(true);
    }
  }, [createTransformationIsSuccess, updateTransformationIsSuccess]);

  useEffect(() => {
    if (createTransformationIsLoading || updateTransformationIsLoading) {
      dispatch(showLoader());
    } else {
      dispatch(hideLoader());
    }
  }, [createTransformationIsLoading, updateTransformationIsLoading]);

  const handleChangeEntity = (
    event: React.SyntheticEvent,
    newValue: string
  ) => {
    if (isDirty) {
      setNextEntity(newValue);
      setOpenChangeAlert(true);
    } else {
      switchEntity(newValue);
    }
  };

  const switchEntity = async (newValue: string) => {
    dispatch(showLoader());
    setEntityData({});
    setEntity(newValue);
    const response = await getTransformation({ entity_name: newValue });
    if (response?.data) {
      setEntityDetails(response?.data);
      setFields(
        response?.data?.fields.map((item: any) => {
          return {
            ...item,
          };
        })
      );
      const initializeFields = (entity: string) => {
        const initialFieldValues: any = {};
        response?.data?.fields?.forEach((field: any, index: number) => {
          initialFieldValues[index] = {
            ...field,
            metadata: {
              ...field.metadata,
            },
          };
        });
        setEntityData({ [entity]: initialFieldValues });
      };
      entity?.length > 0 && initializeFields(newValue);
      setIsUpdate(true);
    } else if (response?.isError) {
      getSelectedEntityDetails({ table_name: newValue });
      setIsUpdate(false);
    } else {
      getSelectedEntityDetails({ table_name: newValue });
      setIsUpdate(false);
    }
    setSelectedFieldIndex(null);
    setIsDirty(false);
    dispatch(hideLoader());
  };

  const handleFieldChange = (index: number, key: string, value: any) => {
    setEntityData((prevData: any) => {
      const updatedData = {
        ...prevData,
        [entity]: {
          ...prevData[entity],
          [index]: {
            ...prevData[entity][index],
            [key]: value,
          },
        },
      };
      return updatedData;
    });
    setIsDirty(true);
  };

  const handleMetadataChange = (index: any, key: string, value: any) => {
    setEntityData((prevData: any) => {
      const updatedData = {
        ...prevData,
        [entity]: {
          ...prevData[entity],
          [index]: {
            ...prevData[entity][index],
            metadata: {
              ...prevData[entity][index].metadata,
              [key]: value,
            },
          },
        },
      };
      if (key === "codelist") {
        updatedData[entity][index].metadata.codelistMapping = [];
      }
      return updatedData;
    });
    setIsDirty(true);
  };

  const handleRowClick = (field: any) => {
    setSelectedFieldIndex(field);
  };

  const handleCodelistButtonClick = async () => {
    const selectedCodelistField = entityData[entity]?.[selectedFieldIndex];
    if (selectedCodelistField?.metadata?.codelistMapping?.length > 0) {
      setCodelistValues(selectedCodelistField.metadata.codelistMapping);
      setOpenCodelistDialog(true);
      return;
    }
    const selectedCodelist =
      entityData[entity]?.[selectedFieldIndex]?.metadata?.codelist ?? null;
    if (selectedCodelist) {
      const response = await getCodelistValues({
        code_list_code: selectedCodelist,
      });
      if (response?.data) {
        const tempCodes = response?.data?.result?.map((ele: any) => {
          return {
            ...ele,
            codelist_destinationField: ele.code_list_value,
            id: ele.code_list_value_code,
          };
        });
        setCodelistValues(tempCodes);
        setOpenCodelistDialog(true);
      }
    }
  };
  const handleCodelistMappingChange = (index: any, field: any, value: any) => {
    setCodelistValues((prevValues: any) =>
      prevValues.map((item: any, idx: any) =>
        idx === index ? { ...item, [field]: value } : item
      )
    );
  };
  const handleSaveCodelistDialog = () => {
    handleMetadataChange(selectedFieldIndex, "codelistMapping", codelistValues);
    setOpenCodelistDialog(false);
  };
  const handleCloseCodelistDialog = () => {
    setOpenCodelistDialog(false);
  };
  const handleConfirmChangeAlert = () => {
    setEntityData((prevData: any) => {
      const updatedData = { ...prevData };
      delete updatedData[entity];
      return updatedData;
    });
    setOpenChangeAlert(false);
    switchEntity(nextEntity);
  };
  const handleCancelChangeAlert = () => {
    setOpenChangeAlert(false);
  };

  const removeNullKeys = (obj: any) => {
    return Object.entries(obj).reduce((acc: any, [key, value]: any) => {
      if (value !== null) {
        if (value && typeof value === "object" && !Array.isArray(value)) {
          value = removeNullKeys(value);
        }
        acc[key] = value;
      }
      return acc;
    }, {});
  };

  const handleSubmit = () => {
    const tempFieldArr = Object.values(entityData?.[entity])?.map(
      (obj: any) => obj
    );
    const cleanFieldData = tempFieldArr.map(removeNullKeys);
    if (isUpdate) {
      updateTransformation({
        data: {
          entity_name: entityDetails?.entity_name,
          fields: cleanFieldData,
        },
      });
    } else {
      createTransformation({
        data: {
          entity_name: entityDetails?.entity_name,
          fields: cleanFieldData,
        },
      });
    }
  };
  const textCaseButtons = [
    { label: "-", value: "-" },
    { label: "AG", value: "AG" },
    { label: "ag", value: "ag" },
    { label: "Ag", value: "Ag" },
  ];

  const handleTextCaseChange = (value: string) => {
    handleMetadataChange(selectedFieldIndex, "textCase", value);
  };
  return (
    <>
      <Loader />
      <Grid container spacing={3}>
        <Grid item md={selectedFieldIndex !== null ? 9 : 12}>
          <Box
            sx={{
              display: "flex",
              '& div[role="tabpanel"]': { flex: 1 },
              maxHeight: "calc(100vh - 268px)",
            }}
          >
            <Tabs
              orientation="vertical"
              variant="scrollable"
              value={entity}
              onChange={handleChangeEntity}
              aria-label="Entity Tabs"
              sx={{
                borderRight: 2,
                borderColor: "divider",
                flex: "0 0 170px",
                "& .MuiTab-root": {
                  alignItems: "flex-start",
                  textAlign: "left",
                  '&[aria-selected="true"]': {
                    background: "rgba(36, 63, 129, 0.07)",
                  },
                },
              }}
            >
              {entityList?.map((tab: any) => (
                <Tab
                  label={tab.entity_name}
                  value={tab.entity_name}
                  key={tab.entity_name}
                />
              ))}
            </Tabs>
            <Box sx={{ pl: 3, width: "100%" }}>
              <TableContainer
                component={Paper}
                sx={{ maxHeight: "calc(100vh - 272px)" }}
              >
                <Table aria-label="Mapping table" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell width={"30%"}>
                        <Stack direction="row" spacing={1}>
                          <BrowserUpdatedIcon color="primary" />{" "}
                          <strong>Target Field</strong>
                        </Stack>
                      </TableCell>
                      <TableCell width={100}>
                        <strong>Data Type</strong>
                      </TableCell>
                      <TableCell width={30} sx={{ background: "#ddd" }}>
                        &nbsp;
                      </TableCell>
                      <TableCell width={"30%"}>
                        <Stack direction="row" spacing={1}>
                          <InputOutlinedIcon color="primary" />
                          <strong>Source Field</strong>
                        </Stack>
                      </TableCell>
                      <TableCell width={100}>
                        <strong>Data Type</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fields?.map((field: any, index: number) => (
                      <TableRow
                        key={index}
                        onClick={() => handleRowClick(index)}
                        className={
                          selectedFieldIndex === index ? "Mui-selected" : ""
                        }
                      >
                        <TableCell component="th" scope="row">
                          <TextField
                            fullWidth
                            size="small"
                            id={`fieldID-${index}`}
                            label={"Target Field"}
                            variant="standard"
                            value={field?.field_name}
                            disabled
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            id={`fieldID-${index}`}
                            label={"Data Type"}
                            variant="standard"
                            value={field?.field_type}
                            disabled
                          />
                        </TableCell>
                        <TableCell sx={{ background: "#ddd" }}>
                          &nbsp;
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            id={`sourceField-${index}`}
                            label={"Source Field"}
                            variant="standard"
                            value={
                              entityData[entity]?.[index]?.source_field_name ||
                              ""
                            }
                            onChange={(e) =>
                              handleFieldChange(
                                index,
                                "source_field_name",
                                e.target.value
                              )
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <FormControl
                            fullWidth
                            size="small"
                            variant="standard"
                            sx={{ width: "100px" }}
                          >
                            <InputLabel id={`sourceDataType-label-${index}`}>
                              Data Type
                            </InputLabel>
                            <Select
                              labelId={`sourceDataType-label-${index}`}
                              id={`sourceDataType-${index}`}
                              value={
                                entityData[entity]?.[index]
                                  ?.source_field_type || ""
                              }
                              label="Source Data Type"
                              onChange={(e) =>
                                handleFieldChange(
                                  index,
                                  "source_field_type",
                                  e.target.value
                                )
                              }
                            >
                              <MenuItem value="string">String</MenuItem>
                              <MenuItem value="number">Number</MenuItem>
                              <MenuItem value="date">Date</MenuItem>
                              <MenuItem value="datetime">Date Time</MenuItem>
                              <MenuItem value="boolean">Boolean</MenuItem>
                              <MenuItem value="codelist">Codelist</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        </Grid>
        <Grid item md={3}>
          {selectedFieldIndex !== null && (
            <Box
              sx={{
                maxHeight: "calc(100vh - 268px)",
                overflowY: "auto",
                pl: "1px",
                pb: 1,
                pr: 1,
                mr: -1,
                position: "relative",
              }}
            >
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  py: 1,
                  background: "white",
                  zIndex: 10,
                  pl: 1,
                  ml: -0.5,
                }}
              >
                <Typography variant="caption" display="block" sx={{ mt: -1 }}>
                  Properties for
                </Typography>
                <Typography
                  variant="h6"
                  gutterBottom
                  component="div"
                  sx={{ mt: -0.75 }}
                >
                  {entityData[entity]?.[selectedFieldIndex]?.field_name}
                </Typography>
                <Tooltip title="Close Config Panel" placement="top">
                  <IconButton
                    color="primary"
                    onClick={() => {
                      setSelectedFieldIndex(null);
                    }}
                    sx={{
                      position: "absolute",
                      right: 0,
                      top: 0,
                      zIndex: 5,
                    }}
                  >
                    <CancelIcon />
                  </IconButton>
                </Tooltip>
                <Divider sx={{ ml: -0.5 }} />
              </Box>
              <Card sx={{ position: "relative" }}>
                <CardContent>
                  <Grid container spacing={2} rowSpacing={2}>
                    <Grid item md={12}>
                      <TextField
                        fullWidth
                        id={`defaultValue-${selectedFieldIndex}`}
                        label="Add Default Value"
                        variant="standard"
                        type={
                          entityData[entity]?.[selectedFieldIndex]
                            ?.field_type === "int"
                            ? "number"
                            : "text"
                        }
                        value={
                          entityData[entity]?.[selectedFieldIndex]?.metadata
                            ?.defaultValue || ""
                        }
                        onChange={(e) =>
                          handleMetadataChange(
                            selectedFieldIndex,
                            "defaultValue",
                            e.target.value
                          )
                        }
                      />
                    </Grid>
                    <Grid item md={12}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">Text Case</FormLabel>
                        <ButtonGroup variant="outlined">
                          {textCaseButtons.map((button) => (
                            <Tooltip
                              title={button.label}
                              placement="top"
                              key={button.value}
                            >
                              <Button
                                className="textTransformNone"
                                onClick={() =>
                                  handleTextCaseChange(button.value)
                                }
                                color={
                                  entityData[entity]?.[selectedFieldIndex]
                                    ?.metadata?.textCase === button.value
                                    ? "primary"
                                    : "inherit"
                                }
                                variant="contained"
                              >
                                {button.label}
                              </Button>
                            </Tooltip>
                          ))}
                        </ButtonGroup>
                      </FormControl>
                    </Grid>
                    <Grid item md={12}>
                      <FormControl className="rowRadioGroup" sx={{ mr: -2 }}>
                        <FormLabel id={`truncate-label-${selectedFieldIndex}`}>
                          Truncate
                        </FormLabel>
                        <RadioGroup
                          row
                          aria-labelledby={`truncate-label-${selectedFieldIndex}`}
                          name={`truncate-group-${selectedFieldIndex}`}
                          value={
                            entityData[entity]?.[selectedFieldIndex]?.metadata
                              ?.truncate || ""
                          }
                          onChange={(e) =>
                            handleMetadataChange(
                              selectedFieldIndex,
                              "truncate",
                              e.target.value
                            )
                          }
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio size="small" />}
                            label="Yes"
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio size="small" />}
                            label="No"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                    {entityData[entity]?.[selectedFieldIndex]?.field_type ===
                      "date" && (
                      <Grid item md={12}>
                        <FormControl
                          fullWidth
                          variant="standard"
                          sx={{ mt: -1 }}
                        >
                          <InputLabel
                            id={`dateFormatDD-label-${selectedFieldIndex}`}
                          >
                            Choose Date Format
                          </InputLabel>
                          <Select
                            labelId={`dateFormatDD-label-${selectedFieldIndex}`}
                            id={`dateFormatDD-${selectedFieldIndex}`}
                            value={
                              entityData[entity]?.[selectedFieldIndex]?.metadata
                                ?.dateFormat || ""
                            }
                            label="Choose Date Format"
                            onChange={(e) =>
                              handleMetadataChange(
                                selectedFieldIndex,
                                "dateFormat",
                                e.target.value
                              )
                            }
                          >
                            <MenuItem value="MM-DD-YYYY">MM-DD-YYYY</MenuItem>
                            <MenuItem value="DD-MM-YYYY">DD-MM-YYYY</MenuItem>
                            <MenuItem value="DD-MMM-YYYY">DD-MMM-YYYY</MenuItem>
                            <MenuItem value="MM-DD-YYYY hh:mm:ss A">
                              MM-DD-YYYY HH:MM:SS
                            </MenuItem>
                            <MenuItem value="DD-MM-YYYY hh:mm:ss A">
                              DD-MM-YYYY HH:MM:SS
                            </MenuItem>
                            <MenuItem value="DD-MMM-YYYY hh:mm:ss A">
                              DD-MMM-YYYY HH:MM:SS
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                    {entityData[entity]?.[selectedFieldIndex]?.field_type ===
                      "datetime" && (
                      <Grid item md={12}>
                        <FormControl
                          fullWidth
                          variant="standard"
                          sx={{ mt: -1 }}
                        >
                          <InputLabel
                            id={`dateTimeFormatDD-label-${selectedFieldIndex}`}
                          >
                            Choose Date Time Format
                          </InputLabel>
                          <Select
                            labelId={`dateTimeFormatDD-label-${selectedFieldIndex}`}
                            id={`dateTimeFormatDD-${selectedFieldIndex}`}
                            value={
                              entityData[entity]?.[selectedFieldIndex]?.metadata
                                ?.dateTimeFormat || ""
                            }
                            label="Choose Date Format"
                            onChange={(e) =>
                              handleMetadataChange(
                                selectedFieldIndex,
                                "dateTimeFormat",
                                e.target.value
                              )
                            }
                          >
                            <MenuItem value="MM-DD-YYYY">MM-DD-YYYY</MenuItem>
                            <MenuItem value="DD-MM-YYYY">DD-MM-YYYY</MenuItem>
                            <MenuItem value="DD-MMM-YYYY">DD-MMM-YYYY</MenuItem>
                            <MenuItem value="MM-DD-YYYY hh:mm:ss A">
                              MM-DD-YYYY HH:MM:SS
                            </MenuItem>
                            <MenuItem value="DD-MM-YYYY hh:mm:ss A">
                              DD-MM-YYYY HH:MM:SS
                            </MenuItem>
                            <MenuItem value="DD-MMM-YYYY hh:mm:ss A">
                              DD-MMM-YYYY HH:MM:SS
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
              {entityData[entity]?.[selectedFieldIndex]?.field_type ===
                "codelist" && (
                <Card sx={{ mt: 2 }}>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item md={12}>
                        <FormControl fullWidth variant="standard">
                          <InputLabel
                            id={`codelistDD-label-${selectedFieldIndex}`}
                          >
                            Choose Codelist
                          </InputLabel>
                          <Select
                            labelId={`codelistDD-label-${selectedFieldIndex}`}
                            id={`codelistDD-${selectedFieldIndex}`}
                            value={
                              entityData[entity]?.[selectedFieldIndex]?.metadata
                                ?.codelist || ""
                            }
                            label="Choose Codelist"
                            onChange={(e) =>
                              handleMetadataChange(
                                selectedFieldIndex,
                                "codelist",
                                e.target.value
                              )
                            }
                          >
                            {codelist?.map((ele: any) => (
                              <MenuItem
                                key={ele?.id}
                                value={ele?.code_list_code}
                              >
                                {ele?.code_list_code}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs>
                        <FormControl fullWidth variant="standard">
                          <InputLabel
                            id={`codelistDD-label-${selectedFieldIndex}`}
                          >
                            Codelist Type
                          </InputLabel>
                          <Select
                            disabled={
                              !entityData[entity]?.[selectedFieldIndex]
                                ?.metadata?.codelist
                            }
                            labelId={`codelistDD-label-${selectedFieldIndex}`}
                            id={`codelistDD-${selectedFieldIndex}`}
                            value={
                              entityData[entity]?.[selectedFieldIndex]?.metadata
                                ?.codelistType || ""
                            }
                            label="Codelist Type"
                            onChange={(e) =>
                              handleMetadataChange(
                                selectedFieldIndex,
                                "codelistType",
                                e.target.value
                              )
                            }
                          >
                            <MenuItem value="code_list_code" disabled>
                              Codelist
                            </MenuItem>
                            <MenuItem value="code_list_value">
                              Display Value
                            </MenuItem>
                            <MenuItem value="code_list_value_code" disabled>
                              Codelist Value code
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      {entityData[entity]?.[selectedFieldIndex]?.metadata
                        ?.codelistType === "code_list_value" && (
                        <Grid item alignItems="flex-end" display="flex">
                          <Tooltip title="Map Codelists Value" placement="top">
                            <Button
                              startIcon={<EditNoteOutlinedIcon />}
                              onClick={handleCodelistButtonClick}
                              variant="contained"
                              sx={{
                                minWidth: "auto",
                                pl: 1.5,
                                pr: 1,
                                "& .MuiButton-startIcon": { mx: 0 },
                              }}
                            />
                          </Tooltip>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              )}
            </Box>
          )}
        </Grid>
      </Grid>
      <Box className="footerStickyBtnWrap dFlex justifyContentEnd">
        <Button
          type="submit"
          variant="contained"
          id="save-connector"
          onClick={handleSubmit}
          disabled={!isDirty}
        >
          Save
        </Button>
      </Box>
      <AlertDialog
        open={openCodelistDialog}
        title={"Codelist Details"}
        handleClose={handleCloseCodelistDialog}
        handleConfirm={handleSaveCodelistDialog}
        cancelBtn="CANCEL"
        confirmBtn="Save"
        maxWidth="md"
      >
        <Typography>
          Codelist:{" "}
          {entityData[entity]?.[selectedFieldIndex]?.metadata?.codelist}
        </Typography>
        <TableContainer>
          <Table aria-label="Codelist Mapping table" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width={"30%"}>Codelist Destination Field</TableCell>
                <TableCell width={"30%"}>Codelist Source Field</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {codelistValues?.map((code: any, index: number) => (
                <TableRow key={code?.id}>
                  <TableCell component="th" scope="row">
                    <TextField
                      fullWidth
                      size="small"
                      id={`codelist_destination_field`}
                      label={"Codelist Destination Field"}
                      variant="standard"
                      disabled
                      value={code?.codelist_destinationField}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      id={`Codelist Source Field`}
                      label={"Codelist Source Field"}
                      variant="standard"
                      value={code.codelist_sourceField}
                      onChange={(e) =>
                        handleCodelistMappingChange(
                          index,
                          "codelist_sourceField",
                          e.target.value
                        )
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </AlertDialog>
      <AlertDialog
        open={openChangeAlert}
        title={"Unsaved Changes"}
        handleClose={handleCancelChangeAlert}
        handleConfirm={handleConfirmChangeAlert}
        cancelBtn="CANCEL"
        confirmBtn="YES"
        maxWidth="xs"
      >
        <Typography>
          You have unsaved changes. Are you sure you want to switch tabs? Your
          changes will be lost.
        </Typography>
      </AlertDialog>
    </>
  );
};

export default TransformationPage;
